/**
 * FeaturesDropdown Component
 *
 * Independent feature access hub in the TradingHub header.
 * Lists all 6 cockpit features with expandable accordion sections
 * for feature-specific actions and settings.
 *
 * Also includes a standalone "Alarms & Reminders" accordion item
 * (not part of FEATURE_AREAS) with inline auto-dismiss setting
 * and access to the AlarmSettingsModal for rule CRUD.
 *
 * Not correlated to cockpit pane layout — provides universal access
 * regardless of which features are visible in the cockpit.
 *
 * T10 builds the framework shell with placeholder content.
 * WU2–WU6 populate actual actions and settings via menuRegistry prop.
 * T14 adds Alarms & Reminders accordion item + AlarmSettingsModal.
 *
 * @see constants/featureArea.ts for FEATURE_AREAS registry
 * @see types/shared/FeatureArea.ts for FeatureAreaId type
 * @see AlarmSettingsModal.tsx for alarm rule CRUD modal
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bell, SlidersHorizontal } from 'lucide-react';

import {
  Accordion, AccordionItem,
  Button, Input,
  Popover, PopoverTrigger, PopoverContent,
} from '@enfomentis/ui';
import { FEATURE_AREAS } from '@/constants/featureArea';
import { liveTradingSettingsService } from '@/services/LiveTradingSettingsService';

import type { AlarmRule, AlarmRuleType } from '@/types/shared/AlarmRule';
import type { FeatureAreaId } from '@/types/shared';

import { AlarmSettingsModal } from './AlarmSettingsModal';

// ===== CONSTANTS =====

/** Short display labels for alarm rule types — exhaustive to fail at compile if new types added */
const RULE_TYPE_SHORT: Record<AlarmRuleType, string> = {
  'candle-close-countdown': 'Candle Close',
  'fixed-interval': 'Fixed Interval',
  'clock-mark': 'Clock Mark',
  'specific-time': 'Specific Time',
};

// ===== TYPES =====

export interface FeaturesDropdownProps {
  /** Registry of feature-specific accordion content provided by WU2–WU6 */
  menuRegistry?: Partial<Record<FeatureAreaId, React.ReactNode>>;
}

// ===== COMPONENT =====

export const FeaturesDropdown: React.FC<FeaturesDropdownProps> = React.memo(({
  menuRegistry,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAlarmSettingsOpen, setIsAlarmSettingsOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Alarm rules state (loaded on popover open, read-only for status summary)
  // ---------------------------------------------------------------------------

  const [alarmRules, setAlarmRules] = useState<AlarmRule[]>([]);

  // ---------------------------------------------------------------------------
  // Auto-dismiss inline state (loaded on popover open, saved on blur)
  // ---------------------------------------------------------------------------

  const [autoDismissInput, setAutoDismissInput] = useState('15');
  const [isDismissLoaded, setIsDismissLoaded] = useState(false);
  const loadedDismissValueRef = useRef('15');
  const isSavingDismissRef = useRef(false);
  const queuedSaveRef = useRef(false);
  const latestInputRef = useRef(autoDismissInput);
  latestInputRef.current = autoDismissInput;

  /** Monotonic counter to discard stale async responses from rapid open/close/open */
  const settingsLoadIdRef = useRef(0);

  // Load auto-dismiss + alarm rules from service when popover opens
  useEffect(() => {
    if (!isOpen) return;
    const loadId = ++settingsLoadIdRef.current;
    setIsDismissLoaded(false);
    liveTradingSettingsService.getOrCreateDefaultSettings()
      .then((settings) => {
        if (loadId !== settingsLoadIdRef.current) return;
        const rawSeconds = (settings.alarmAutoDismissMs ?? 15000) / 1000;
        const clamped = Math.max(1, Math.min(30, Math.round(rawSeconds)));
        const value = String(clamped);
        setAutoDismissInput(value);
        loadedDismissValueRef.current = value;
        setIsDismissLoaded(true);
        setAlarmRules(settings.alarmRules ?? []);
      })
      .catch(() => {
        if (loadId !== settingsLoadIdRef.current) return;
        setAutoDismissInput('15');
        loadedDismissValueRef.current = '15';
        setIsDismissLoaded(true);
        setAlarmRules([]);
      });
  }, [isOpen]);

  // Derived validation
  const dismissN = Number(autoDismissInput);
  const isDismissInvalid = autoDismissInput.trim() === ''
    || !Number.isFinite(dismissN)
    || !Number.isInteger(dismissN)
    || dismissN < 1
    || dismissN > 30;

  // Validate + save a dismiss value (used by blur handler and queued-save flush).
  // Reads from refs only — safe to call from .finally() without closure staleness.
  const saveDismissValue = useCallback((value: string): void => {
    const n = Number(value);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 30) return;
    if (value === loadedDismissValueRef.current) return;
    const previousValue = loadedDismissValueRef.current;
    isSavingDismissRef.current = true;
    liveTradingSettingsService.updateAlarmConfig({
      alarmAutoDismissMs: n * 1000,
    }).then(() => {
      loadedDismissValueRef.current = value;
    }).catch(() => {
      if (!queuedSaveRef.current && latestInputRef.current === value) {
        setAutoDismissInput(previousValue);
      }
    }).finally(() => {
      isSavingDismissRef.current = false;
      // Flush at most one queued save (capped to prevent recursive churn)
      if (queuedSaveRef.current) {
        queuedSaveRef.current = false;
        saveDismissValue(latestInputRef.current);
      }
    });
  }, []);

  // Save on blur — only if loaded, not already saving, valid, and changed
  const handleDismissBlur = useCallback((): void => {
    if (!isDismissLoaded) return;
    if (isSavingDismissRef.current) {
      queuedSaveRef.current = true;
      return;
    }
    saveDismissValue(autoDismissInput);
  }, [isDismissLoaded, autoDismissInput, saveDismissValue]);

  // ---------------------------------------------------------------------------
  // Enabled alarms summary (derived from loaded rules)
  // ---------------------------------------------------------------------------

  const enabledRules = useMemo(
    () => alarmRules.filter((r) => r.enabled).sort((a, b) => a.order - b.order),
    [alarmRules],
  );
  const alarmSubtitle = enabledRules.length === 0 ? '(0)' : `(${enabledRules.length})`;

  return (
    <>
      <Popover placement="bottom-end" isOpen={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger>
          <Button variant="light" size="sm" isIconOnly aria-label="Feature settings">
            <SlidersHorizontal size={18} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-2 min-w-65 max-w-65">
          <Accordion isCompact selectionMode="multiple" variant="light">
            {[
              ...FEATURE_AREAS.map((area) => (
                <AccordionItem
                  key={area.id}
                  title={area.label}
                  startContent={<area.Icon size={16} className={area.iconColorClass} />}
                >
                  {/* Clicks on action buttons inside registry content close the popover */}
                  <div onClickCapture={() => { setIsOpen(false); }}>
                    {menuRegistry?.[area.id] ?? (
                      <p className="text-xs text-default-400">No actions or settings available yet</p>
                    )}
                  </div>
                </AccordionItem>
              )),
              <AccordionItem
              key="alarms"
              textValue={`Alarms & Reminders ${alarmSubtitle}`}
              title={<>Alarms & Reminders <span className="text-sm text-text-secondary">{alarmSubtitle}</span></>}
              startContent={<Bell size={16} className="text-warning" />}
            >
              <div className="flex flex-col gap-2">
                {enabledRules.length > 0 && (
                  <div className="flex flex-col gap-0.5 mb-1 min-w-0">
                    {enabledRules.slice(0, 3).map((rule) => (
                      <p key={rule.id} className="text-xs text-text-secondary truncate">
                        {rule.message} — {RULE_TYPE_SHORT[rule.type]}
                      </p>
                    ))}
                    {enabledRules.length > 3 && (
                      <p className="text-xs text-text-tertiary">+ {enabledRules.length - 3} more</p>
                    )}
                  </div>
                )}
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  className="w-full"
                  onPress={() => {
                    setIsAlarmSettingsOpen(true);
                    setIsOpen(false);
                  }}
                >
                  Manage
                </Button>
                <Input
                  size="sm"
                  variant="bordered"
                  label="Auto-dismiss"
                  labelPlacement='outside-left'
                  type="number"
                  min={1}
                  max={30}
                  isDisabled={!isDismissLoaded}
                  value={autoDismissInput}
                  onValueChange={setAutoDismissInput}
                  onBlur={handleDismissBlur}
                  className="justify-between"
                  classNames={{ input: 'max-md:text-base!' , label: 'text-sm' }}
                  aria-label="Auto-dismiss seconds"
                  isInvalid={isDismissInvalid}
                  errorMessage={isDismissInvalid ? '1–30' : undefined}
                  endContent={<span className="text-default-400">sec</span>}
                />
              </div>
            </AccordionItem>,
            ]}
          </Accordion>
        </PopoverContent>
      </Popover>

      {/* Mounted outside Popover to survive popover teardown */}
      <AlarmSettingsModal
        isOpen={isAlarmSettingsOpen}
        onClose={() => setIsAlarmSettingsOpen(false)}
      />
    </>
  );
});

FeaturesDropdown.displayName = 'FeaturesDropdown';
